import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  FileText, 
  Search, 
  Plus, 
  Download, 
  Trash2, 
  Edit2, 
  Filter,
  Eye,
  Folder,
  Image as ImageIcon,
  File
} from 'lucide-react';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import { usePermissions } from '../../../utils/usePermissions';
import '../GestionRH.css';

const API = (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '').replace(/\/api$/, '') + '/api';
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export default function DocumentsRH() {
  const { hasPermission } = usePermissions();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('tous');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      // Logic: In a real app, this would fetch from /api/rh/documents
      // For now, using mock or existing documents logic from previous state
      const mockDocs = [
        { id: 1, title: 'Contrat Type CDI', category: 'Modèles', date: '2024-01-15', image: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?auto=format&fit=crop&q=80&w=300' },
        { id: 2, title: 'Règlement Intérieur', category: 'RH', date: '2024-02-10', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=300' },
        { id: 3, title: 'Fiche Standard Evaluation', category: 'Modèles', date: '2024-03-01', image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?auto=format&fit=crop&q=80&w=300' },
      ];
      setDocuments(mockDocs);
    } catch (err) {
      console.error('Error fetching documents:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredDocs = useMemo(() => {
    return documents.filter(doc => {
      const matchesSearch = doc.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCat = filterCategory === 'tous' || doc.category === filterCategory;
      return matchesSearch && matchesCat;
    });
  }, [documents, searchTerm, filterCategory]);

  const handleAddDocument = () => {
    Swal.fire({
      title: 'Ajouter un Document',
      html: `
        <div class="rh-modal-form">
          <div class="rh-input-group">
            <label>Titre *</label>
            <input id="doc-title" class="rh-modal-input" placeholder="Ex: Contrat de travail">
          </div>
          <div class="rh-input-group">
            <label>Catégorie *</label>
            <select id="doc-cat" class="rh-modal-input">
              <option value="Modèles">Modèles & Prototypes</option>
              <option value="RH">Documents Internes</option>
              <option value="Archives">Archives</option>
            </select>
          </div>
          <div class="rh-input-group">
            <label>Fichier (Lien image ou simulation)</label>
            <input id="doc-img" class="rh-modal-input" placeholder="https://...">
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Ajouter',
      confirmButtonColor: '#FFD700',
      preConfirm: () => {
        const title = document.getElementById('doc-title').value;
        const category = document.getElementById('doc-cat').value;
        if (!title || !category) {
          Swal.showValidationMessage('Veuillez remplir les champs obligatoires');
          return false;
        }
        return { title, category, date: new Date().toISOString().split('T')[0], image: document.getElementById('doc-img').value || 'https://via.placeholder.com/300' };
      }
    }).then(result => {
      if (result.isConfirmed) {
        setDocuments([...documents, { id: Date.now(), ...result.value }]);
        Swal.fire('Ajouté !', '', 'success');
      }
    });
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Supprimer ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#E53E3E'
    }).then(res => {
      if (res.isConfirmed) {
        setDocuments(documents.filter(d => d.id !== id));
      }
    });
  };

  if (loading) return <div className="rh-dashboard-content"><LoadingSpinner /></div>;

  return (
    <div className="animate-fade-in">
      <div className="rh-table-card">
        <div className="rh-table-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div className="rh-hero-icon" style={{ background: '#3182CE', width: '45px', height: '45px', borderRadius: '12px' }}>
              <Folder size={24} color="white" />
            </div>
            <div>
              <h2 style={{ margin: 0 }}>Gestion Documentaire</h2>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--rh-text-muted)' }}>Modèles RH et prototypes centralisés</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <div className="rh-search-wrapper" style={{ width: '250px' }}>
              <Search className="rh-icon-search" size={16} />
              <input 
                type="text" 
                className="rh-search-input" 
                placeholder="Rechercher un document..." 
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <button className="rh-add-btn" onClick={handleAddDocument}>
              <Plus size={18} /> Ajouter
            </button>
          </div>
        </div>

        <div className="rh-docs-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '25px', marginTop: '25px' }}>
          {filteredDocs.map(doc => (
            <div key={doc.id} className="rh-doc-card" style={{ background: 'white', borderRadius: '20px', border: '1.5px solid var(--rh-border)', overflow: 'hidden', transition: 'all 0.3s' }}>
              <div style={{ height: '160px', position: 'relative', overflow: 'hidden' }}>
                <img src={doc.image} alt={doc.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                  <span className="rh-type-badge" style={{ background: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>{doc.category}</span>
                </div>
              </div>
              <div style={{ padding: '20px' }}>
                <h3 style={{ margin: '0 0 5px 0', fontSize: '16px', color: 'var(--rh-blue-dark)' }}>{doc.title}</h3>
                <p style={{ margin: '0 0 15px 0', fontSize: '13px', color: 'var(--rh-text-muted)' }}>Ajouté le {doc.date}</p>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '15px', borderTop: '1px solid var(--rh-border)' }}>
                  <button className="rh-view-all" style={{ padding: 0, background: 'none', border: 'none', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <Eye size={14} /> Consulter
                    </div>
                  </button>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button className="rh-action-btn edit" style={{ width: '32px', height: '32px' }}>
                      <Download size={14} />
                    </button>
                    <button className="rh-action-btn delete" style={{ width: '32px', height: '32px' }} onClick={() => handleDelete(doc.id)}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filteredDocs.length === 0 && (
            <div style={{ gridColumn: 'span 3', textAlign: 'center', padding: '50px', color: 'var(--rh-text-muted)' }}>
              Aucun document trouvé.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
